import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Sparkles, CheckCircle2 } from 'lucide-react';

const SCALE = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly Agree' },
];

const Starfield = () => {
  const stars = useMemo(() => Array.from({ length: 60 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: Math.random() * 2 + 1,
    duration: Math.random() * 4 + 3,
    delay: Math.random() * 5,
    opacity: Math.random() * 0.5 + 0.15,
  })), []);
  return (
    <div className="starfield">
      {stars.map(s => (
        <div
          key={s.id}
          className="star"
          style={{
            left: s.left, top: s.top,
            width: `${s.size}px`, height: `${s.size}px`,
            animationDuration: `${s.duration}s`, animationDelay: `${s.delay}s`,
            opacity: s.opacity,
          }}
        />
      ))}
    </div>
  );
};

const SacredBackground = () => (
  <>
    <div className="noise-overlay" />
    <Starfield />
    <div className="sacred-geometry" style={{ top: '10%', right: '5%', width: '500px', height: '500px' }}>
      <svg viewBox="0 0 200 200" width="100%" height="100%">
        <circle cx="100" cy="100" r="80" fill="none" stroke="#C6B28F" strokeWidth="0.3" />
        <circle cx="100" cy="100" r="60" fill="none" stroke="#C6B28F" strokeWidth="0.2" />
        <circle cx="100" cy="100" r="40" fill="none" stroke="#C6B28F" strokeWidth="0.3" />
        <circle cx="100" cy="40" r="40" fill="none" stroke="#7A8274" strokeWidth="0.2" />
        <circle cx="100" cy="160" r="40" fill="none" stroke="#7A8274" strokeWidth="0.2" />
        <circle cx="40" cy="100" r="40" fill="none" stroke="#7A8274" strokeWidth="0.2" />
        <circle cx="160" cy="100" r="40" fill="none" stroke="#7A8274" strokeWidth="0.2" />
      </svg>
    </div>
    <div className="ethereal-glow-gold animate-drift" style={{ top: '-150px', right: '-100px', zIndex: 0 }} />
    <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-120px', left: '-80px', zIndex: 0 }} />
    <div className="ethereal-glow-warm" style={{ top: '30%', left: '50%', transform: 'translateX(-50%)', zIndex: 0 }} />
    <div className="floating-orb floating-orb-gold" style={{ width: 80, height: 80, top: '15%', right: '20%', animationDuration: '8s', zIndex: 0 }} />
    <div className="floating-orb floating-orb-moss" style={{ width: 60, height: 60, bottom: '25%', left: '15%', animationDuration: '10s', animationDelay: '2s', zIndex: 0 }} />
  </>
);

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [phase, setPhase] = useState('welcome'); // welcome | test | results
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Do not auto-redirect — allow retake from profile
  }, [user, navigate]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/onboarding/questions');
      setQuestions(res.data.questions);
      setPhase('test');
      setCurrentIdx(0);
    } catch {
      toast.error('Could not load the test. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (val) => {
    const q = questions[currentIdx];
    const updated = { ...answers, [q.id]: val };
    setAnswers(updated);

    // Auto-advance after short delay for smooth UX
    setTimeout(() => {
      if (currentIdx < questions.length - 1) {
        setCurrentIdx(currentIdx + 1);
      }
    }, 250);
  };

  const goPrev = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  };

  const goNext = () => {
    if (currentIdx < questions.length - 1) setCurrentIdx(currentIdx + 1);
  };

  const submitTest = async () => {
    setLoading(true);
    try {
      const res = await api.post('/onboarding/submit', { answers });
      setResult(res.data);
      setPhase('results');
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit the test');
    } finally {
      setLoading(false);
    }
  };

  const skipTest = async () => {
    setLoading(true);
    try {
      await api.post('/onboarding/skip');
      await checkAuth();
      toast.success('You can take the test anytime from your Profile.');
      navigate('/', { replace: true });
    } catch {
      toast.error('Could not skip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const finishOnboarding = () => {
    navigate('/', { replace: true });
  };

  if (phase === 'welcome') {
    return (
      <div className="min-h-screen bg-sand relative overflow-hidden" data-testid="onboarding-welcome">
        <SacredBackground />
        <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
          <div className="max-w-2xl text-center animate-fade-up">
            <div className="inline-block mb-8">
              <div className="relative w-24 h-24 mx-auto">
                <div className="absolute inset-0 rounded-full bg-gold/20 animate-breathe" />
                <div className="absolute inset-3 rounded-full bg-gold/15 animate-breathe" style={{ animationDelay: '0.5s' }} />
                <div className="absolute inset-6 rounded-full bg-gold/25 animate-breathe flex items-center justify-center" style={{ animationDelay: '1s' }}>
                  <Sparkles size={20} className="text-gold" />
                </div>
              </div>
            </div>

            <p className="text-gold font-sans text-xs tracking-[0.35em] uppercase mb-4">
              Welcome, {user?.name?.split(' ')[0] || 'Seeker'}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-dark leading-[1.1] tracking-tight mystical-heading shimmer-text">
              Before we begin...
            </h1>
            <div className="mystical-divider my-8 mx-auto max-w-[100px]" />
            <p className="font-sans text-base sm:text-lg text-text-muted leading-relaxed max-w-xl mx-auto mb-4">
              Manifestation begins with self-knowing. Your coach works best when attuned to who you already are — and to the version of you that is becoming.
            </p>
            <p className="font-sans text-base text-text-muted leading-relaxed max-w-xl mx-auto mb-10">
              Take a few quiet minutes. Answer honestly. There are no wrong answers — only a clearer mirror.
            </p>

            <div className="mystical-card p-6 sm:p-8 mb-8 text-left">
              <div className="flex items-start gap-4">
                <div className="mt-1">
                  <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center">
                    <span className="font-serif text-gold text-lg">36</span>
                  </div>
                </div>
                <div>
                  <p className="font-serif text-xl text-dark mb-1">Enneagram Self-Inquiry</p>
                  <p className="font-sans text-sm text-text-muted leading-relaxed">
                    Thirty-six short reflections. About 5–7 minutes. Your results will reveal your dominant pattern — its gifts, its pitfalls — and guide how your coach meets you.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={loadQuestions}
                disabled={loading}
                data-testid="onboarding-begin-button"
                className="h-14 px-10 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-[0.2em] uppercase btn-hover"
              >
                {loading ? 'Loading...' : 'Begin the Inquiry'}
                <ArrowRight size={16} className="ml-3" />
              </Button>
              <Button
                onClick={skipTest}
                disabled={loading}
                variant="ghost"
                data-testid="onboarding-skip-button"
                className="h-14 px-8 text-text-muted hover:text-dark font-sans text-xs tracking-wider uppercase"
              >
                Skip for now
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'test') {
    const q = questions[currentIdx];
    const progress = ((currentIdx + 1) / questions.length) * 100;
    const current = answers[q?.id];
    const allAnswered = questions.every(qq => answers[qq.id]);

    return (
      <div className="min-h-screen bg-sand relative overflow-hidden" data-testid="onboarding-test">
        <SacredBackground />
        <div className="relative z-10 min-h-screen flex flex-col p-6">
          {/* Progress */}
          <div className="max-w-2xl w-full mx-auto pt-8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gold font-sans text-xs tracking-[0.25em] uppercase">
                Question {currentIdx + 1} of {questions.length}
              </p>
              <p className="text-text-muted font-sans text-xs tracking-wider">
                {Math.round(progress)}% complete
              </p>
            </div>
            <div className="h-[2px] bg-black/8 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-gold/50 to-gold rounded-full"
                style={{ width: `${progress}%`, transition: 'width 0.4s ease' }}
                data-testid="progress-bar"
              />
            </div>
          </div>

          {/* Question */}
          <div className="flex-1 flex items-center justify-center max-w-2xl w-full mx-auto py-10">
            <div key={q.id} className="w-full animate-fade-up">
              <p className="text-gold font-sans text-[10px] tracking-[0.35em] uppercase mb-4 text-center">
                Reflect gently
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-dark leading-snug text-center mb-10 mystical-heading">
                {q.text}
              </h2>

              <div className="space-y-3" data-testid="answer-options">
                {SCALE.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => selectAnswer(opt.value)}
                    data-testid={`answer-${opt.value}`}
                    className={`w-full p-4 sm:p-5 rounded-xl border text-left flex items-center gap-4 ${
                      current === opt.value
                        ? 'border-gold/50 bg-gold/10 gold-border-glow'
                        : 'border-black/8 bg-bone/50 hover:border-gold/25 hover:bg-bone/70'
                    }`}
                    style={{ transition: 'border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease' }}
                  >
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        current === opt.value ? 'border-gold bg-gold' : 'border-black/15'
                      }`}
                      style={{ transition: 'all 0.2s ease' }}
                    >
                      {current === opt.value && <CheckCircle2 size={14} className="text-bone" />}
                    </div>
                    <span className="font-sans text-base text-dark">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="max-w-2xl w-full mx-auto pb-6">
            <div className="flex items-center justify-between gap-3">
              <Button
                onClick={goPrev}
                disabled={currentIdx === 0}
                variant="ghost"
                data-testid="prev-question-button"
                className="h-12 px-5 text-text-muted hover:text-dark font-sans text-xs tracking-wider uppercase disabled:opacity-30"
              >
                <ArrowLeft size={14} className="mr-2" /> Previous
              </Button>

              {currentIdx === questions.length - 1 ? (
                <Button
                  onClick={submitTest}
                  disabled={!allAnswered || loading}
                  data-testid="submit-test-button"
                  className="h-12 px-8 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.2em] uppercase btn-hover"
                >
                  {loading ? 'Revealing...' : 'Reveal my pattern'}
                  <Sparkles size={14} className="ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={goNext}
                  disabled={!current}
                  data-testid="next-question-button"
                  className="h-12 px-6 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-wider uppercase btn-hover disabled:opacity-30"
                >
                  Next <ArrowRight size={14} className="ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results phase
  if (phase === 'results' && result) {
    const dom = result.dominant;
    return (
      <div className="min-h-screen bg-sand relative overflow-hidden" data-testid="onboarding-results">
        <SacredBackground />
        <div className="relative z-10 min-h-screen py-12 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center animate-fade-up mb-12">
              <p className="text-gold font-sans text-xs tracking-[0.35em] uppercase mb-4">
                Your Dominant Pattern
              </p>
              <h1
                className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-dark leading-[1.1] tracking-tight mystical-heading shimmer-text"
                data-testid="dominant-type-name"
              >
                Type {dom.type} — {dom.name}
              </h1>
              <p className="font-sans text-base text-text-muted italic mt-3">{dom.subtitle}</p>
              <div className="mystical-divider my-8 mx-auto max-w-[100px]" />
              <p className="font-sans text-base sm:text-lg text-dark leading-relaxed max-w-2xl mx-auto">
                {dom.description}
              </p>
            </div>

            {/* Top 3 */}
            <div className="mystical-card p-6 sm:p-8 mb-6 animate-fade-up delay-100" data-testid="top-3-section">
              <p className="text-gold font-sans text-xs tracking-[0.25em] uppercase mb-5">Your Top 3 Patterns</p>
              <div className="space-y-3">
                {result.top_3.map((t, i) => (
                  <div
                    key={t.type}
                    className={`flex items-center justify-between p-4 rounded-xl ${
                      i === 0 ? 'bg-gold/10 border border-gold/30' : 'bg-bone/60 border border-black/8'
                    }`}
                    data-testid={`top-type-${i}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-serif ${
                        i === 0 ? 'bg-gold text-dark' : 'bg-black/8 text-dark'
                      }`}>
                        {t.type}
                      </div>
                      <div>
                        <p className="font-serif text-lg text-dark leading-tight">{t.name}</p>
                        <p className="font-sans text-xs text-text-muted italic">{t.subtitle}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-serif text-2xl text-dark">{t.score}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Positive aspects */}
            <div className="mystical-card p-6 sm:p-8 mb-6 animate-fade-up delay-200">
              <p className="text-gold font-sans text-xs tracking-[0.25em] uppercase mb-4">Your Natural Gifts</p>
              <ul className="space-y-3">
                {dom.positive_aspects.map((item, i) => (
                  <li key={i} className="flex gap-3 font-sans text-sm text-dark leading-relaxed">
                    <span className="text-gold mt-1">✦</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pitfalls */}
            <div className="mystical-card p-6 sm:p-8 mb-6 animate-fade-up delay-300">
              <p className="text-gold font-sans text-xs tracking-[0.25em] uppercase mb-4">Likely Shadows</p>
              <ul className="space-y-3">
                {dom.pitfalls.map((item, i) => (
                  <li key={i} className="flex gap-3 font-sans text-sm text-text-muted leading-relaxed">
                    <span className="text-gold/70 mt-1">◌</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Manifestation insight */}
            <div
              className="mystical-card p-6 sm:p-8 mb-10 animate-fade-up delay-400 text-bone"
              style={{
                background: '#2C302B',
                borderColor: 'rgba(198, 178, 143, 0.3)',
              }}
            >
              <p className="text-gold font-sans text-xs tracking-[0.25em] uppercase mb-4">Manifestation Path</p>
              <p className="font-serif text-lg sm:text-xl text-bone leading-relaxed italic">
                "{dom.manifestation_insight}"
              </p>
            </div>

            <div className="text-center animate-fade-up delay-500">
              <Button
                onClick={finishOnboarding}
                data-testid="finish-onboarding-button"
                className="h-14 px-10 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-[0.2em] uppercase btn-hover"
              >
                Enter Your Practice <ArrowRight size={16} className="ml-3" />
              </Button>
              <p className="text-text-muted font-sans text-xs tracking-wider mt-4">
                Your profile will be woven into every coaching session.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
